import { subscribeKey } from 'valtio/utils';
import { calculateLength } from '~/helpers/unitHelpers';
import { lengthStore } from '../../Widgets/Measure/store';
import type { CanvasEvent } from '../CanvasEvent';
import DrawLineObject from '../line/DrawLineObject';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import VertexObject from '../VertexObject';
import { fontSize, getSegmentData, measureColor, measureLineDash, measureLineWidth, padding } from './common';

export default class DrawMeasureDistanceObject extends DrawLineObject {
  constructor(id: string, surface: SurfaceBase, onMeasureDistance: (p: LngLat[]) => void) {
    super(id, surface, onMeasureDistance);

    subscribeKey(lengthStore, 'unitType', () => this.renderAll());
  }

  get length() {
    const { unitType, unitSystem } = lengthStore;
    return calculateLength({
      unitType,
      unitSystem,
      geometry: {
        type: 'LineString',
        coordinates: [
          ...this.vertices.map((v) => [v.lngLat.lng, v.lngLat.lat]),
          [this.pointerVertex.lngLat.lng, this.pointerVertex.lngLat.lat],
        ],
      },
    });
  }

  onSurfaceTap = (e: CanvasEvent) => {
    if (e.type === 'dblTap') return;

    const v = new VertexObject(`v-${this.vertices.length}`, this.surface, {
      point: e.point,
      lngLat: e.lngLat,
    });

    this.vertices.push(v);
    this.surface.addObject(v);
  };

  calculateTextPoint = (textWidth: number): Point => {
    const offset = 25;
    const lastPoint = this.vertices[this.vertices.length - 1].point;
    const cursorPoint = this.pointerVertex.point;

    if (cursorPoint.x >= lastPoint.x) {
      return { x: cursorPoint.x + offset, y: cursorPoint.y };
    } else {
      return { x: cursorPoint.x - textWidth - offset, y: cursorPoint.y };
    }
  };

  renderSegmentText = (ctx: CanvasRenderingContext2D): void => {
    for (let i = 1; i <= this.vertices.length; i++) {
      let v1: VertexObject;
      let v2: VertexObject;

      if (i === this.vertices.length) {
        v1 = this.vertices[this.vertices.length - 1];
        v2 = this.pointerVertex;
      } else {
        v1 = this.vertices[i - 1];
        v2 = this.vertices[i];
      }

      const { unitType, unitSystem } = lengthStore;
      const s = getSegmentData(v1, v2, unitType, unitSystem);

      const w = v2.point.x - v1.point.x;
      const h = v2.point.y - v1.point.y;

      const segmentCanvasLength = Math.hypot(w, h);

      ctx.save();
      ctx.font = `bold ${fontSize}px arial`;
      const lengthText = `${s.length.value.toLocaleString()} ${s.length.unit}`;
      const textWidth = ctx.measureText(lengthText).width;

      // only render segment text if the text width is less than the segment width
      if (textWidth + padding <= segmentCanvasLength) {
        ctx.fillStyle = measureColor;

        ctx.translate(s.point.x, s.point.y);
        ctx.rotate(s.rotation);

        ctx.beginPath();
        ctx.roundRect(
          -textWidth / 2 - padding / 2,
          -fontSize - 2 * padding,
          textWidth + padding,
          fontSize + padding,
          [5],
        );
        ctx.fill();

        ctx.textBaseline = 'top';
        ctx.textAlign = 'center';
        ctx.fillStyle = 'white';
        ctx.fillText(`${s.length.value.toLocaleString()} ${s.length.unit}`, 0, -fontSize - 1.5 * padding);
      }

      ctx.restore();
    }
  };

  render(ctx: CanvasRenderingContext2D): void {
    if (this.vertices.length === 0) return;

    // change strokeStyle before ctx.save() so it changes the vertices' colors as well.
    ctx.strokeStyle = measureColor;
    ctx.save();
    ctx.lineWidth = measureLineWidth;
    ctx.setLineDash(measureLineDash);
    const { point } = this.vertices[0];
    ctx.moveTo(point.x, point.y);

    for (let i = 1; i < this.vertices.length; i++) {
      const p = this.vertices[i].point;
      ctx.lineTo(p.x, p.y);
    }

    ctx.lineTo(this.pointerVertex.point.x, this.pointerVertex.point.y);
    ctx.stroke();

    const l = this.length;
    lengthStore.length = l.value;

    ctx.font = `bold ${fontSize}px arial`;
    const lengthText = `${l.value.toLocaleString()} ${l.unit}`;
    const textWidth = ctx.measureText(lengthText).width;
    const textPoint = this.calculateTextPoint(textWidth);
    ctx.fillStyle = measureColor;
    ctx.beginPath();
    ctx.roundRect(textPoint.x, textPoint.y, textWidth + padding, fontSize + padding, [5]);
    ctx.fill();
    ctx.textBaseline = 'top';
    ctx.fillStyle = 'white';
    ctx.fillText(`${l.value.toLocaleString()} ${l.unit}`, textPoint.x + padding / 2, textPoint.y + padding / 2);
    ctx.restore();

    this.renderSegmentText(ctx);
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    // nothing to draw
  }
}
